import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import PaymentBadges from "@/components/feature/PaymentBadges";
import type { DiscountState, PointsDiscountState, RedeemCouponState } from "@/pages/checkout/types";

// Valid discount codes — key: lowercase code, value: percent off
const VALID_CODES: Record<string, number> = {
  "ebdaa-taif": 10,
};

interface OrderSummaryProps {
  discount: DiscountState;
  onDiscountApply: (state: DiscountState) => void;
  pointsDiscount?: PointsDiscountState;
  isPlatinum?: boolean;
  platinumDiscountPercent?: number;
  redeemCoupon?: RedeemCouponState;
  onRedeemCouponApply?: (state: RedeemCouponState) => void;
}

export default function OrderSummary({ discount, onDiscountApply, pointsDiscount, isPlatinum = false, platinumDiscountPercent = 5, redeemCoupon, onRedeemCouponApply }: OrderSummaryProps) {
  const { items, totalItems, totalPrice, updateQty, removeItem } = useCart();
  const { isLoggedIn, user } = useAuth();
  const [codeInput, setCodeInput] = useState("");
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [redeemInput, setRedeemInput] = useState("");
  const [redeemChecking, setRedeemChecking] = useState(false);
  const redeemInputRef = useRef<HTMLInputElement>(null);

  const discountAmount = Math.round(totalPrice * (discount.percent / 100));
  const platinumDiscountAmount = isPlatinum ? Math.round(totalPrice * (platinumDiscountPercent / 100)) : 0;
  const pointsDiscountAmount = pointsDiscount?.applied ? pointsDiscount.discountAmount : 0;
  const redeemCouponDiscountAmount = redeemCoupon?.status === "valid"
    ? Math.round(totalPrice * (redeemCoupon.discountPercent / 100))
    : 0;
  const finalPrice = Math.max(0, totalPrice - discountAmount - platinumDiscountAmount - pointsDiscountAmount - redeemCouponDiscountAmount);
  const expectedPoints = Math.floor(finalPrice / 10);

  const handleRedeemApply = async () => {
    if (!redeemInput.trim() || !onRedeemCouponApply) return;
    setRedeemChecking(true);
    try {
      const code = redeemInput.trim().toUpperCase();
      const { data, error } = await supabase
        .from("redeem_coupons")
        .select("id, coupon_code, discount_percent, status, expires_at, customer_id")
        .eq("coupon_code", code)
        .maybeSingle();

      if (error || !data) {
        onRedeemCouponApply({ code, discountPercent: 0, status: "invalid" });
      } else if (data.status === "used") {
        onRedeemCouponApply({ code, discountPercent: 0, status: "used" });
      } else if (data.status === "expired" || new Date(data.expires_at) < new Date()) {
        onRedeemCouponApply({ code, discountPercent: 0, status: "expired" });
      } else if (isLoggedIn && user && data.customer_id !== user.id) {
        onRedeemCouponApply({ code, discountPercent: 0, status: "invalid" });
      } else {
        onRedeemCouponApply({ code, discountPercent: data.discount_percent, status: "valid", couponId: data.id });
      }
    } catch {
      onRedeemCouponApply({ code: redeemInput.trim(), discountPercent: 0, status: "invalid" });
    }
    setRedeemChecking(false);
  };

  const handleRedeemRemove = () => {
    setRedeemInput("");
    onRedeemCouponApply?.({ code: "", discountPercent: 0, status: "idle" });
    setTimeout(() => redeemInputRef.current?.focus(), 100);
  };

  const handleApply = () => {
    if (!codeInput.trim()) return;
    setChecking(true);
    // Simulate slight delay for feel
    setTimeout(() => {
      const normalized = codeInput.trim().toLowerCase();
      const percent = VALID_CODES[normalized];
      if (percent !== undefined) {
        const amount = Math.round(totalPrice * (percent / 100));
        onDiscountApply({ code: codeInput.trim(), percent, amount, status: "valid" });
      } else {
        onDiscountApply({ code: codeInput.trim(), percent: 0, amount: 0, status: "invalid" });
      }
      setChecking(false);
    }, 600);
  };

  const handleRemoveDiscount = () => {
    setCodeInput("");
    onDiscountApply({ code: "", percent: 0, amount: 0, status: "idle" });
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-400/15 rounded-full">
          <i className="ri-shopping-bag-3-line text-amber-400 text-sm"></i>
        </div>
        <h2 className="text-white font-bold text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
          ملخص الطلب
          <span className="text-gray-500 font-normal text-sm mr-2">({totalItems} منتج)</span>
        </h2>
      </div>

      {/* Items */}
      <div className="flex flex-col gap-0 rounded-2xl overflow-hidden border border-white/5 bg-[#111]">
        {items.map((item, idx) => (
          <div
            key={item.id}
            className={`flex items-center gap-4 px-4 py-4 ${idx < items.length - 1 ? "border-b border-white/5" : ""}`}
          >
            <Link to={`/product/${item.id}`} className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-[#1a1a1a] cursor-pointer">
              <img src={item.image} alt={item.nameAr} className="w-full h-full object-cover object-top" />
            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-amber-400/70 text-xs mb-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>{item.category}</p>
              <p className="text-white text-sm font-semibold truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>{item.nameAr}</p>
              <p className="text-gray-500 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {item.price.toLocaleString()} ر.س × {item.qty}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <span className="text-amber-400 font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {(item.price * item.qty).toLocaleString()} ر.س
              </span>
              <div className="flex items-center border border-white/10 rounded-full overflow-hidden h-7">
                <button
                  onClick={() => updateQty(item.id, item.qty - 1)}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-400 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-xs"></i>
                </button>
                <span className="w-6 text-center text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {item.qty}
                </span>
                <button
                  onClick={() => updateQty(item.id, item.qty + 1)}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-400 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-xs"></i>
                </button>
              </div>
            </div>
            <button
              onClick={() => removeItem(item.id)}
              className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-red-400 transition-colors cursor-pointer rounded-full hover:bg-red-400/10 flex-shrink-0"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          </div>
        ))}
      </div>

      {/* Discount Code */}
      <div className="rounded-2xl border border-white/5 bg-[#111] px-4 py-4">
        <p className="text-gray-400 text-xs font-medium mb-3 flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
          <i className="ri-coupon-3-line text-amber-400"></i>
          كود الخصم
        </p>

        {discount.status === "valid" ? (
          /* Applied state */
          <div className="flex items-center justify-between gap-3 bg-green-400/8 border border-green-400/25 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center bg-green-400/20 rounded-full flex-shrink-0">
                <i className="ri-check-line text-green-400 text-xs"></i>
              </div>
              <div>
                <p className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {discount.code}
                </p>
                <p className="text-green-400/70 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  خصم {discount.percent}% — وفّرت {discountAmount.toLocaleString()} ر.س
                </p>
              </div>
            </div>
            <button
              onClick={handleRemoveDiscount}
              className="text-gray-500 hover:text-red-400 transition-colors cursor-pointer text-xs whitespace-nowrap flex items-center gap-1"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-close-line"></i>
              إزالة
            </button>
          </div>
        ) : (
          /* Input state */
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  if (discount.status === "invalid") {
                    onDiscountApply({ code: "", percent: 0, amount: 0, status: "idle" });
                  }
                }}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
                placeholder="أدخل كود الخصم"
                className={`flex-1 bg-[#1a1a1a] border rounded-xl px-4 py-2.5 text-white text-sm outline-none transition-colors placeholder-gray-600 ${
                  discount.status === "invalid"
                    ? "border-red-500/50 focus:border-red-500/70"
                    : "border-white/10 focus:border-amber-400/50"
                }`}
                style={{ fontFamily: "'Cairo', sans-serif", direction: "ltr", textAlign: "right" }}
              />
              <button
                onClick={handleApply}
                disabled={checking || !codeInput.trim()}
                className="bg-amber-400 hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold px-4 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                {checking ? (
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                ) : (
                  <i className="ri-check-line text-sm"></i>
                )}
                تطبيق
              </button>
            </div>
            {discount.status === "invalid" && (
              <p className="text-red-400 text-xs flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-error-warning-line"></i>
                كود الخصم غير صحيح أو منتهي الصلاحية
              </p>
            )}
          </div>
        )}
      </div>

      {/* Redeem Coupon — only for logged-in users */}
      {isLoggedIn && onRedeemCouponApply && (
        <div className="rounded-2xl border border-amber-400/20 bg-[#111] px-4 py-4">
          <p className="text-gray-400 text-xs font-medium mb-3 flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-exchange-dollar-line text-amber-400"></i>
            كوبون استبدال النقاط
            <a href="/redeem" target="_blank" className="text-amber-400/60 hover:text-amber-400 text-[10px] mr-auto flex items-center gap-0.5 transition-colors cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>
              احصل على كوبون
              <i className="ri-external-link-line text-[10px]"></i>
            </a>
          </p>

          {redeemCoupon?.status === "valid" ? (
            <div className="flex items-center justify-between gap-3 bg-amber-400/8 border border-amber-400/25 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center bg-amber-400/20 rounded-full flex-shrink-0">
                  <i className="ri-check-line text-amber-400 text-xs"></i>
                </div>
                <div>
                  <p className="text-amber-400 text-xs font-bold font-mono">{redeemCoupon.code}</p>
                  <p className="text-amber-400/70 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    خصم {redeemCoupon.discountPercent}% — وفّرت {redeemCouponDiscountAmount.toLocaleString()} ر.س
                  </p>
                </div>
              </div>
              <button onClick={handleRedeemRemove} className="text-gray-500 hover:text-red-400 transition-colors cursor-pointer text-xs whitespace-nowrap flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-close-line"></i>إزالة
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  ref={redeemInputRef}
                  type="text"
                  value={redeemInput}
                  onChange={(e) => {
                    setRedeemInput(e.target.value.toUpperCase());
                    if (redeemCoupon && redeemCoupon.status !== "idle") onRedeemCouponApply?.({ code: "", discountPercent: 0, status: "idle" });
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleRedeemApply()}
                  placeholder="POINTS-XXXX-XXXX"
                  className={`flex-1 bg-[#1a1a1a] border rounded-xl px-4 py-2.5 text-white text-xs outline-none transition-colors placeholder-gray-600 font-mono ${
                    redeemCoupon?.status === "invalid" || redeemCoupon?.status === "used" || redeemCoupon?.status === "expired"
                      ? "border-red-500/50 focus:border-red-500/70"
                      : "border-amber-400/20 focus:border-amber-400/50"
                  }`}
                  dir="ltr"
                />
                <button
                  onClick={handleRedeemApply}
                  disabled={redeemChecking || !redeemInput.trim()}
                  className="bg-amber-400/20 hover:bg-amber-400/30 border border-amber-400/30 disabled:opacity-50 disabled:cursor-not-allowed text-amber-400 font-bold px-4 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  {redeemChecking ? <i className="ri-loader-4-line animate-spin text-sm"></i> : <i className="ri-check-line text-sm"></i>}
                  تطبيق
                </button>
              </div>
              {redeemCoupon?.status === "invalid" && (
                <p className="text-red-400 text-xs flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-error-warning-line"></i>الكوبون غير صحيح أو لا ينتمي لحسابك
                </p>
              )}
              {redeemCoupon?.status === "used" && (
                <p className="text-red-400 text-xs flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-error-warning-line"></i>هذا الكوبون تم استخدامه مسبقاً
                </p>
              )}
              {redeemCoupon?.status === "expired" && (
                <p className="text-red-400 text-xs flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-error-warning-line"></i>هذا الكوبون منتهي الصلاحية
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Totals */}
      <div className="rounded-2xl border border-white/5 bg-[#111] px-5 py-4 flex flex-col gap-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400" style={{ fontFamily: "'Cairo', sans-serif" }}>المجموع الفرعي</span>
          <span className="text-white" style={{ fontFamily: "'Cairo', sans-serif" }}>{totalPrice.toLocaleString()} ر.س</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400" style={{ fontFamily: "'Cairo', sans-serif" }}>رسوم الشحن</span>
          <span className="text-green-400 font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>مجاني</span>
        </div>

        {isPlatinum && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-cyan-300 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-vip-diamond-line text-xs"></i>
              خصم البلاتيني ({platinumDiscountPercent}%)
            </span>
            <span className="text-cyan-300 font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              — {platinumDiscountAmount.toLocaleString()} ر.س
            </span>
          </div>
        )}

        {discount.status === "valid" && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-green-400 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-coupon-3-line text-xs"></i>
              خصم ({discount.percent}%)
            </span>
            <span className="text-green-400 font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              — {discountAmount.toLocaleString()} ر.س
            </span>
          </div>
        )}

        {pointsDiscount?.applied && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-400 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-vip-crown-line text-xs"></i>
              نقاط مكافآت ({pointsDiscount.pointsToRedeem.toLocaleString()} نقطة)
            </span>
            <span className="text-amber-400 font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              — {pointsDiscountAmount.toLocaleString()} ر.س
            </span>
          </div>
        )}

        {redeemCoupon?.status === "valid" && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-400 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-exchange-dollar-line text-xs"></i>
              كوبون استبدال ({redeemCoupon.discountPercent}%)
            </span>
            <span className="text-amber-400 font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              — {redeemCouponDiscountAmount.toLocaleString()} ر.س
            </span>
          </div>
        )}

        <div className="border-t border-white/5 pt-3 flex items-center justify-between mt-1">
          <span className="text-white font-bold text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>الإجمالي</span>
          <div className="text-left">
            {(discount.status === "valid" || pointsDiscount?.applied || isPlatinum || redeemCoupon?.status === "valid") && (
              <p className="text-gray-500 text-xs line-through text-left" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {totalPrice.toLocaleString()} ر.س
              </p>
            )}
            <span className="text-amber-400 font-black text-2xl" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {finalPrice.toLocaleString()} ر.س
            </span>
          </div>
        </div>
      </div>

      {/* Points earned preview */}
      {expectedPoints > 0 && (
        <div className="flex items-center gap-3 bg-amber-400/6 border border-amber-400/20 rounded-2xl px-4 py-3">
          <div className="w-9 h-9 flex items-center justify-center bg-amber-400/15 rounded-xl flex-shrink-0">
            <i className="ri-vip-crown-line text-amber-400 text-base"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-amber-400 text-xs font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
              ستكسب <span className="text-base">+{expectedPoints}</span> نقطة مكافأة
            </p>
            <p className="text-amber-400/60 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {isLoggedIn ? "تُضاف لحسابك تلقائياً عند توصيل الطلب" : "سجّل دخولك لتجميع نقاطك"}
            </p>
          </div>
          {!isLoggedIn && (
            <a
              href="/auth"
              className="flex-shrink-0 text-amber-400 hover:text-amber-300 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              تسجيل الدخول
            </a>
          )}
        </div>
      )}

      {/* Trust badges */}
      <div className="rounded-2xl border border-white/5 bg-[#111] px-4 py-3">
        <p className="text-gray-500 text-[10px] mb-2 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
          <i className="ri-shield-check-line text-amber-400"></i>
          طرق الدفع المقبولة
        </p>
        <PaymentBadges size="sm" />
      </div>

      {/* Security note */}
      <div className="flex items-start gap-2 px-1">
        <i className="ri-lock-2-line text-green-400 text-sm mt-0.5 flex-shrink-0"></i>
        <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
          بياناتك محمية وآمنة. لن تُشارك مع أي طرف ثالث.
        </p>
      </div>
    </div>
  );
}
